import {
  isFileExtracting,
  EXTRACTION_STATUS,
} from '../config/brandLifecycleConfig'
import { SECTION_ACTION, toSectionStatus } from '../config/draftReviewConfig'

/*
 * Every shape on this boundary comes from "Extraction - API Contract" on
 * Confluence (CONTENT/1365937796). Base path for all of them:
 *   /brand_dna/v1/brands/{brandUuid}
 *
 * The UI never sends a request_id or an extraction_job_id: the brand uuid in
 * the route resolves to its own latest non-cancelled job. Both ids come back
 * on every response, and are kept here only for support and log correlation.
 */

const cleanValue = (value) => `${value ?? ''}`.trim()

const byDisplayOrder = (first, second) =>
  (first.displayOrder || 0) - (second.displayOrder || 0)

/*
 * POST /extraction — kick extraction off. The response is not specified
 * beyond the ids, and the real confirmation is the status endpoint moving off
 * NOT_STARTED, so only the document count is read, for the toast.
 */
export const mapExtractionStart = (response = {}) => ({
  documentCount:
    Number(
      response.document_count ??
        response.documentCount ??
        response.documents?.length,
    ) || 0,
  jobId: cleanValue(response.extraction_job_id),
  requestId: cleanValue(response.request_id),
})

/*
 * GET /extraction/status — the poll target, deliberately cheap.
 * {
 *   "status": "PROCESSING", "review_ready": false,
 *   "submitted_at": "…", "completed_at": null,
 *   "estimated_remaining_seconds": 188, "poll_after_ms": 5000,
 *   "documents": [{ "file_id": "…", "file_name": "…",
 *                   "document_category": "Style Guide",
 *                   "status": "EXTRACTING" }],
 *   "error": null
 * }
 * A brand with no extraction job answers 200 with status NOT_STARTED, so the
 * Drafts tab polls unconditionally and never has to treat 404 as a state.
 */
const mapStatusFile = (file = {}) => ({
  category: cleanValue(file.document_category),
  id: cleanValue(file.file_id),
  isExtracting: isFileExtracting(cleanValue(file.status)),
  name: cleanValue(file.file_name),
  status: cleanValue(file.status),
})

export const mapExtractionStatus = (response = {}) => ({
  documents: (response.documents || []).map(mapStatusFile),
  // `code` is DS_EXTRACTION_FAILED or EXTRACTION_RESULT_TIMEOUT.
  error: cleanValue(response.error?.message),
  errorCode: cleanValue(response.error?.code),
  jobId: cleanValue(response.extraction_job_id),
  // Server-tuned cadence, so the beat can change without a UI release.
  pollAfterMs: Number(response.poll_after_ms) || 0,
  requestId: cleanValue(response.request_id),
  // The failed job can be re-run in place rather than started afresh.
  retryAvailable: response.retry_available === true,
  // An explicit boolean so the UI never has to hardcode the enum.
  reviewReady: Boolean(response.review_ready),
  status: cleanValue(response.status) || EXTRACTION_STATUS.NOT_STARTED,
})

/*
 * GET /sections — the review payload behind the Drafts screen.
 *
 * A section reports two things: `status`, the reviewer's decision (DRAFT,
 * APPROVED, REJECTED), and `state`, the shape extraction left it in (EMPTY,
 * SINGLE, CONFLICT). draftReviewConfig collapses the pair into the one status
 * the cards render from.
 */
const mapSourceFiles = (sourceFiles = []) =>
  sourceFiles
    .map((file) => ({
      id: cleanValue(file.file_id),
      name: cleanValue(file.file_name),
    }))
    .filter(({ name }) => name)

const mapCandidate = (candidate = {}, index) => {
  const sourceFiles = mapSourceFiles(candidate.source_files)

  return {
    // The id is what a SELECT sends back; the index fallback only keeps React
    // keys stable in a malformed payload.
    id: cleanValue(candidate.candidate_id) || `candidate-${index}`,
    optionNumber: Number(candidate.option_number) || index + 1,
    // The heading above each option in the conflict card.
    sourceFileName: sourceFiles.map(({ name }) => name).join(' · '),
    sourceFiles,
    value: cleanValue(candidate.content),
  }
}

// The document a settled section's value came from, for the line under its
// title. Reads the chosen candidate when there is one, else the only one.
const getSourceLabel = (candidates, selectedCandidateId) => {
  if (candidates.length === 0) return ''
  const selected =
    candidates.find(({ id }) => id === selectedCandidateId) ||
    (candidates.length === 1 ? candidates[0] : null)
  return selected?.sourceFileName || ''
}

const mapSection = (section = {}) => {
  const candidates = (section.candidates || [])
    .map(mapCandidate)
    .filter((candidate) => candidate.value)
  const selectedCandidateId = cleanValue(section.selected_candidate_id)

  return {
    candidates,
    comment: cleanValue(section.comment),
    displayOrder: Number(section.display_order) || 0,
    id: cleanValue(section.section_review_id),
    isMandatory: Boolean(section.mandatory),
    reviewedAt: cleanValue(section.reviewed_at),
    reviewedBy: cleanValue(section.reviewed_by),
    // Goes straight back out on the next write so the backend can reject one
    // made against a stale copy. Each item carries its own.
    rowVersion: Number(section.row_version) || 0,
    sectionCode: cleanValue(section.section_code),
    selectedCandidateId,
    sourceLabel: getSourceLabel(candidates, selectedCandidateId),
    status: toSectionStatus(section),
    title: cleanValue(section.section_name),
    value: cleanValue(section.content),
  }
}

const mapGroup = (group = {}) => ({
  displayOrder: Number(group.display_order) || 0,
  id: cleanValue(group.group_code),
  label: cleanValue(group.group_name),
  sections: (group.sections || [])
    .map(mapSection)
    .filter(({ id }) => id)
    .sort(byDisplayOrder),
})

const mapDocument = (document = {}) => ({
  category: cleanValue(document.document_category),
  id: cleanValue(document.file_id),
  name: cleanValue(document.file_name),
})

// The counters come from the API rather than being recounted here: the
// contract requires the same service method to produce them for the header,
// the review body and every write response, so they cannot drift.
const mapSummary = (summary = {}) => ({
  approvedCount: Number(summary.approved_count ?? summary.approved) || 0,
  canSendToPublish: Boolean(summary.can_send_to_publish),
  documentCount: Number(summary.document_count) || 0,
  emptyCount: Number(summary.empty) || 0,
  pendingConflictCount:
    Number(summary.pending_conflict_count ?? summary.pending_conflict) || 0,
  rejectedCount: Number(summary.rejected_count ?? summary.rejected) || 0,
  sectionCount: Number(summary.extracted_section_count) || 0,
  totalSectionCount: Number(summary.total_section_count ?? summary.total) || 0,
})

export const mapDraftReview = (response = {}) => ({
  ...mapSummary(response.summary),
  documents: (response.documents || []).map(mapDocument),
  groups: (response.section_groups || [])
    .map(mapGroup)
    .filter(({ id }) => id)
    .sort(byDisplayOrder),
  jobId: cleanValue(response.extraction_job_id),
  requestId: cleanValue(response.request_id),
})

/*
 * PUT /sections — one endpoint, list payload. A single edit is a list of one;
 * a group approve is a list of that group's ids. Actions may be mixed, and
 * the whole call is one transaction: one stale row fails all of it.
 *
 * APPROVE takes selected_candidate_id and content in the same item, so
 * select–edit–approve is a single round trip.
 */
export const toSectionWriteBody = (decisions = []) => ({
  sections: decisions.map(
    ({ action, candidateId, comment, rowVersion, sectionId, value }) => ({
      action,
      comment: comment || null,
      // Explicit nulls rather than omitted keys: the contract shows every
      // field present on every item.
      content: value === undefined ? null : value,
      row_version: rowVersion || 0,
      section_review_id: sectionId,
      // EDIT sets content only; it never carries a candidate selection.
      ...(action === SECTION_ACTION.EDIT
        ? {}
        : { selected_candidate_id: candidateId || null }),
    }),
  ),
})

// 200 returns the updated sections in the same shape as the review payload,
// plus refreshed counters so the header updates from the same call.
export const mapSectionWrite = (response = {}) => ({
  ...mapSummary(response.summary),
  updated: (response.updated || []).map(mapSection).filter(({ id }) => id),
})

/*
 * 409 SECTION_REVIEW_CONFLICT names every offending item and its current row
 * version, so only those rows need refreshing rather than the whole screen.
 * 422 uses the same shape for an APPROVE with blank content.
 */
export const mapSectionWriteFailure = (response = {}) => ({
  code: cleanValue(response.code),
  failures: (response.failures || []).map((failure = {}) => ({
    code: cleanValue(failure.code),
    currentRowVersion: Number(failure.current_row_version) || 0,
    sectionId: cleanValue(failure.section_review_id),
  })),
  message: cleanValue(response.message),
})

// GET /details — the brand's AssetHub project and its two statuses, read
// fresh for the details page. See brandLifecycleConfig.
export const mapBrandDetails = (response = {}) => ({
  assetHubProjectUuid: cleanValue(response.assethub_project_uuid),
  ingestionStatus: cleanValue(response.brand_ingestion_status),
  lifecycleStatus: cleanValue(response.brand_lifecycle_status),
})
